import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Compass, ChevronDown, ChevronRight, Copy, ExternalLink, CheckCircle2, Loader2, Lightbulb, Rocket, Camera, FileDown, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import ConceptStarterForm from './ConceptStarterForm';
import WebsiteBlueprint from './WebsiteBlueprint';
import LogoConcepts from './LogoConcepts';
import { colorName } from './colorNames';
import { loadGoogleFonts } from './brandData';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

function Pick({ items, value, onPick, render, testid, cols = 'sm:grid-cols-2 lg:grid-cols-3' }) {
  return (
    <div className={`grid ${cols} gap-2`} data-testid={testid}>
      {items.map((it, i) => (
        <button key={i} type="button" onClick={() => onPick(i)} data-testid={`${testid}-${i}`}
          className={`text-left rounded-xl border-2 p-3 transition-colors ${value === i ? 'border-[#1D4ED8] bg-[#EFF6FF]' : 'border-[#E2E8F0] bg-white hover:border-[#1D4ED8]/50'}`}>
          {render(it, value === i)}
        </button>
      ))}
    </div>
  );
}

const H = ({ children }) => <h5 className="font-bold text-sm text-[#0A0F1C] mb-2 mt-5 first:mt-0">{children}</h5>;
const copy = (t) => { navigator.clipboard?.writeText(t || ''); toast.success('Copied'); };

function ConceptResults({ vision, base, q, onUpdate, invite }) {
  const c = vision.concept;
  const [sel, setSel] = useState({ name_index: 0, tagline_index: 0, domain_index: 0, palette_index: 0, font_index: 0 });
  const [applying, setApplying] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}/concept-pdf${q}`, { responseType: 'blob' });
      const name = ((r.headers['content-disposition'] || '').match(/filename="([^"]+)"/) || [])[1] || 'website-concept.pdf';
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not build the PDF'); }
    finally { setPdfBusy(false); }
  };
  useEffect(() => { loadGoogleFonts((c.font_pairs || []).flatMap((f) => [f.heading, f.body]).filter(Boolean)); }, [c.font_pairs]);
  const set = (k) => (i) => setSel((s) => ({ ...s, [k]: i }));
  const chosenName = (c.business_names || [])[sel.name_index]?.name || vision.business_name || '';

  const apply = async () => {
    setApplying(true);
    try { const r = await ax.post(`${BACKEND}${base}/${vision.vision_id}/concept/apply${q}`, sel); onUpdate(r.data); toast.success('Concept applied to your vision. Scroll down to review everything.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not apply'); }
    finally { setApplying(false); }
  };

  return (
    <div className="mt-6 border-t border-[#FED7AA] pt-5" data-testid="concept-results">
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#F97316]">Your concept</div>
          <button type="button" onClick={downloadPdf} disabled={pdfBusy} data-testid="concept-pdf-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0F1C] text-white text-xs font-bold hover:bg-[#1e293b] disabled:opacity-60">
            {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Download PDF
          </button>
        </div>
        <p className="text-sm text-[#0A0F1C] leading-relaxed" data-testid="concept-summary">{c.concept_summary}</p>
        {c.passion_to_profit && <p className="text-sm text-[#475569] leading-relaxed mt-2"><b className="text-[#0A0F1C]">How this earns:</b> {c.passion_to_profit}</p>}
      </div>

      <H>Pick a business name</H>
      <Pick items={c.business_names || []} value={sel.name_index} onPick={set('name_index')} testid="concept-names"
        render={(n) => (<><div className="font-black text-[#0A0F1C]">{n.name}</div><div className="text-xs text-[#64748B] mt-0.5">{n.why}</div></>)} />

      <H>Website domain ideas</H>
      <Pick items={c.domains || []} value={sel.domain_index} onPick={set('domain_index')} testid="concept-domains"
        render={(d) => (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0"><div className="font-bold text-sm text-[#0A0F1C] font-mono truncate">{d.domain}</div><div className="text-[11px] text-[#64748B]">{d.note}</div></div>
            <a href={`https://www.namecheap.com/domains/registration/results/?domain=${encodeURIComponent(d.domain)}`} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} className="shrink-0 text-[11px] font-bold text-[#1D4ED8] inline-flex items-center gap-0.5 hover:underline">Check <ExternalLink size={10} /></a>
          </div>
        )} />

      <H>Tagline</H>
      <Pick items={c.taglines || []} value={sel.tagline_index} onPick={set('tagline_index')} testid="concept-taglines" cols="sm:grid-cols-2"
        render={(t) => <div className="text-sm font-semibold text-[#0A0F1C]">“{t}”</div>} />

      <div className="grid md:grid-cols-2 gap-3 mt-5">
        {[['Mission statement', c.mission_statement, 'concept-mission'], ['Vision statement', c.vision_statement, 'concept-vision']].map(([t, v, id]) => (
          <div key={id} className="bg-white rounded-xl border border-[#E2E8F0] p-3" data-testid={id}>
            <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase text-[#64748B]">{t}</span><button type="button" onClick={() => copy(v)} className="text-[#94A3B8] hover:text-[#1D4ED8]"><Copy size={12} /></button></div>
            <p className="text-sm text-[#0A0F1C]">{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 mt-3" data-testid="concept-bio">
        <div className="flex justify-between items-center mb-1"><span className="text-xs font-bold uppercase text-[#64748B]">Your bio</span><button type="button" onClick={() => copy(c.bio_long)} className="text-[#94A3B8] hover:text-[#1D4ED8]"><Copy size={12} /></button></div>
        <p className="text-sm font-semibold text-[#0A0F1C]">{c.bio_short}</p>
        <p className="text-sm text-[#475569] mt-2 leading-relaxed whitespace-pre-wrap">{c.bio_long}</p>
      </div>

      {(c.offers || []).length > 0 && (<>
        <H>Services & offers for your site</H>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="concept-offers">
          {c.offers.map((o, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-3">
              <div className="font-bold text-sm text-[#0A0F1C]">{o.name}</div>
              <div className="text-xs text-[#475569] mt-0.5">{o.description}</div>
              <div className="flex items-center justify-between mt-2"><span className="text-xs font-bold text-emerald-700">{o.price_idea}</span><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#C2410C]">{o.cta}</span></div>
            </div>
          ))}
        </div>
      </>)}

      <H>Color palette</H>
      <Pick items={c.palettes || []} value={sel.palette_index} onPick={set('palette_index')} testid="concept-palettes" cols="sm:grid-cols-2"
        render={(p) => (<>
          <div className="flex h-10 rounded-lg overflow-hidden mb-1.5">{(p.colors || []).map((x, j) => <span key={j} className="flex-1" style={{ background: x.hex }} title={`${colorName(x.hex)} ${x.hex}`} />)}</div>
          <div className="text-sm font-bold text-[#0A0F1C]">{p.name}</div>
          <div className="text-[11px] text-[#64748B]">{(p.colors || []).map((x) => colorName(x.hex)).join(' · ')}</div>
        </>)} />

      <H>Fonts</H>
      <Pick items={c.font_pairs || []} value={sel.font_index} onPick={set('font_index')} testid="concept-fonts" cols="sm:grid-cols-2"
        render={(f) => (<>
          <div className="text-xl font-bold text-[#0A0F1C]" style={{ fontFamily: f.heading }}>{chosenName || f.heading}</div>
          <div className="text-sm text-[#475569]" style={{ fontFamily: f.body }}>{f.heading} + {f.body}. {f.why}</div>
        </>)} />

      <div className="mt-5 bg-white rounded-xl border border-[#E2E8F0] p-4"><LogoConcepts vision={vision} base={base} q={q} name={chosenName} onUpdate={onUpdate} maxRoots={invite ? 4 : 14} /></div>

      <div className="mt-5 bg-white rounded-xl border border-[#E2E8F0] p-4"><WebsiteBlueprint blueprint={c.website_blueprint} /></div>

      <div className="grid md:grid-cols-3 gap-3 mt-5">
        {[[Lightbulb, 'Conversion tips', c.conversion_tips, 'concept-tips'], [Camera, 'Photo plan', c.photo_plan, 'concept-photos'], [Rocket, 'Next steps', c.next_steps, 'concept-next']].map(([Icon, t, arr, id]) => (
          <div key={id} className="bg-white rounded-xl border border-[#E2E8F0] p-3" data-testid={id}>
            <div className="flex items-center gap-1.5 mb-1.5"><Icon size={13} className="text-[#F97316]" /><span className="text-xs font-bold uppercase text-[#64748B]">{t}</span></div>
            <ul className="space-y-1">{(arr || []).map((x, i) => <li key={i} className="text-xs text-[#334155] leading-relaxed">• {x}</li>)}</ul>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 bg-[#0A0F1C] rounded-xl p-4">
        <div className="flex-1 min-w-[220px] text-white"><div className="font-bold text-sm">Happy with your picks?</div><div className="text-xs text-slate-300">We'll fill your name, palette, fonts, vision, pages and features below. Nothing you typed gets erased.</div></div>
        <button type="button" onClick={apply} disabled={applying} data-testid="concept-apply-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F97316] text-white font-bold text-sm hover:bg-[#EA580C] disabled:opacity-60">
          {applying ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Apply to my vision
        </button>
      </div>
    </div>
  );
}

function InviteDialog({ open, onClose, vision }) {
  const [email, setEmail] = useState(vision.invite_email || vision.client_email || '');
  const [name, setName] = useState(vision.client_name || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [remOn, setRemOn] = useState(!vision.invite_reminders_off);
  const [reminding, setReminding] = useState(false);
  const toggleRem = async () => {
    try { await ax.post(`${BACKEND}/api/admin/website-vision/${vision.vision_id}/invite/reminders`, { enabled: !remOn }); setRemOn(!remOn); toast.success(!remOn ? 'Reminders on' : 'Reminders off'); }
    catch { toast.error('Could not update reminders'); }
  };
  const remindNow = async () => {
    setReminding(true);
    try { await ax.post(`${BACKEND}/api/admin/website-vision/${vision.vision_id}/invite/remind`); toast.success('Reminder sent'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send reminder'); }
    finally { setReminding(false); }
  };
  const send = async () => {
    setBusy(true);
    try { const r = await ax.post(`${BACKEND}/api/admin/website-vision/${vision.vision_id}/invite`, { email, name, message }); setLink(r.data.invite_url); if (r.data.email_sent) toast.success(`Invite sent to ${r.data.invite_email}`); else toast.warning('The email could not be delivered. Copy the link below and send it yourself.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send invite'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite client to design</DialogTitle>
          <DialogDescription>They'll get an email link (no login) to answer the questions. The AI designs their concept right away and you're notified.</DialogDescription></DialogHeader>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name" data-testid="invite-name" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" data-testid="invite-email" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Personal note (optional)" data-testid="invite-message" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
        {link && (
          <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="invite-link">
            <a href={link} target="_blank" rel="noopener noreferrer" className="truncate flex-1 text-emerald-800 font-mono hover:underline">{link}</a>
            <button type="button" onClick={() => copy(link)} className="text-emerald-700"><Copy size={13} /></button>
          </div>
        )}
        {(vision.invite_sent_at || link) && !vision.invite_submitted_at && (
          <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-xs text-[#475569] space-y-2" data-testid="invite-reminders">
            <div className="flex items-center justify-between gap-2">
              <span>Auto reminders on day 3 and day 7{vision.invite_reminders_sent ? ` · ${vision.invite_reminders_sent} sent` : ''}</span>
              <button type="button" onClick={toggleRem} data-testid="invite-reminders-toggle"
                className={`relative w-9 h-5 rounded-full transition-colors ${remOn ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${remOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <button type="button" onClick={remindNow} disabled={reminding} data-testid="invite-remind-now"
              className="inline-flex items-center gap-1 font-bold text-[#C2410C] hover:underline disabled:opacity-60">
              {reminding ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reminder now
            </button>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-slate-100">Close</button>
          <button type="button" onClick={send} disabled={busy || !email.trim()} data-testid="invite-send"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {link ? 'Resend invite' : 'Send invite'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ConceptStudio({ vision, base, q, onUpdate, onSave, isAdmin = false, invite = false }) {
  const [open, setOpen] = useState(invite || !!vision.concept || vision.category === 'passion');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [designing, setDesigning] = useState(vision.concept_status === 'generating');
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = () => {
    const started = Date.now();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}${q}`).catch(() => null);
      if (!r) return;
      if (r.data.concept_status !== 'generating' || Date.now() - started > 300000) {
        clearInterval(pollRef.current); setDesigning(false); onUpdate(r.data);
        if (r.data.concept_status === 'ready') toast.success('Your concept is ready!');
        else toast.error(r.data.concept_error || 'This is taking longer than usual, please try again.');
      }
    }, 5000);
  };
  useEffect(() => { if (vision.concept_status === 'generating') poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const design = async () => {
    setDesigning(true);
    try { await ax.post(`${BACKEND}${base}/${vision.vision_id}/concept${q}`); poll(); }
    catch (e) { setDesigning(false); toast.error(e.response?.data?.detail || 'Could not start'); }
  };

  return (
    <div className="rounded-2xl border-2 border-[#FED7AA] bg-gradient-to-br from-[#FFF7ED] to-white mb-5 overflow-hidden" data-testid="concept-studio">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="concept-studio-toggle" className="w-full flex items-center gap-3 p-5 text-left">
        <span className="w-10 h-10 rounded-xl bg-[#F97316] text-white flex items-center justify-center shrink-0"><Compass size={20} /></span>
        <span className="flex-1">
          <span className="block font-black text-base text-[#0A0F1C]">No idea where to start? We'll design it for you.</span>
          <span className="block text-xs text-[#64748B]">No logo, colors or photos needed. Share your passion or skill and get names, domains, a logo, palette, bio, offers and a website layout that converts.</span>
        </span>
        {open ? <ChevronDown size={18} className="text-[#94A3B8]" /> : <ChevronRight size={18} className="text-[#94A3B8]" />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-[#FED7AA] rounded-xl px-3 py-2">
              <span className="text-xs text-[#64748B] flex-1 min-w-[200px]">Want the client to answer these on their own?{vision.invite_sent_at ? ` Invite sent ${new Date(vision.invite_sent_at).toLocaleDateString()}${vision.invite_submitted_at ? ', answered ✓' : ''}.` : ''}</span>
              <button type="button" onClick={() => setInviteOpen(true)} data-testid="concept-invite-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F97316] text-[#C2410C] text-xs font-bold hover:bg-[#FFF7ED]"><Send size={12} /> Invite client to design</button>
            </div>
          )}
          {isAdmin && <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} vision={vision} />}
          <ConceptStarterForm starter={vision.starter} onSave={(st) => onSave({ starter: st })} onDesign={design} designing={designing} hasConcept={!!vision.concept} />
          {vision.concept && !designing && <ConceptResults vision={vision} base={base} q={q} onUpdate={onUpdate} invite={invite} />}
        </div>
      )}
    </div>
  );
}
